/**
 * Configuration Help Component
 * Displays helpful information when Azure Voice Live is not configured
 */

import React, { useState, useEffect } from 'react';
import { getApiUrl } from '../utils/apiUtils';
import { WarningIcon } from './SvgConsolidated/SvgIcons';

interface ConfigStatus {
  azure_configured: boolean;
  voice_live_ready: boolean;
  age_profiles_loaded: boolean;
  environment_valid: boolean;
  missing_variables: string[];
  recommendations: string[];
}

interface ConfigurationHelpProps {
  className?: string;
}

export default function ConfigurationHelp({ className = '' }: ConfigurationHelpProps) {
  const [configStatus, setConfigStatus] = useState<ConfigStatus | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchConfigStatus();
  }, []);

  const fetchConfigStatus = async () => {
    try {
      const response = await fetch(getApiUrl('/config/status'));
      if (response.ok) {
        const data = await response.json();
        setConfigStatus(data);
      }
    } catch (error) {
      console.error('Failed to fetch configuration status:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return null;
  }

  if (!configStatus || configStatus.azure_configured) {
    return null; // Don't show if Azure is properly configured
  }

  return (
    <div className={`bg-amber-50 border-l-4 border-amber-500 p-4 ${className}`}>
      <div className="flex items-start">
        <div className="flex-shrink-0">
           <WarningIcon className="w-4 h-4 text-gray-500" />
        </div>
        <div className="ml-3 flex-1">
          <h3 className="text-sm font-medium text-amber-800">
            Voice Features Configuration Required
          </h3>
          <div className="mt-2 text-sm text-amber-700">
            <p>Azure Voice Live credentials need to be configured in the backend.</p>
            
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="mt-2 text-amber-800 hover:text-amber-900 font-semibold flex items-center"
            >
              {isExpanded ? '▼' : '▶'} {isExpanded ? 'Hide' : 'Show'} Configuration Details
            </button>

            {isExpanded && (
              <div className="mt-3 space-y-3">
                {configStatus.missing_variables.length > 0 && (
                  <div>
                    <p className="font-semibold mb-1">Missing Environment Variables:</p>
                    <ul className="list-disc list-inside space-y-1 ml-2">
                      {configStatus.missing_variables.map((variable, index) => (
                        <li key={index} className="font-mono text-xs bg-amber-100 inline-block px-2 py-1 rounded mr-2 mb-1">
                          {variable}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {configStatus.recommendations.length > 0 && (
                  <div>
                    <p className="font-semibold mb-1">Recommendations:</p>
                    <ol className="list-decimal list-inside space-y-1">
                      {configStatus.recommendations.map((rec, index) => (
                        <li key={index}>{rec}</li>
                      ))}
                    </ol>
                  </div>
                )}

                <div className="mt-4 p-3 bg-white rounded border border-amber-200">
                  <p className="font-semibold text-amber-900 mb-2">Quick Setup Guide:</p>
                  <ol className="text-xs space-y-2 text-amber-800">
                    <li>
                      <strong>1. Get Azure Credentials:</strong>
                      <br />Navigate to Azure Portal → AI Services → Your Resource
                    </li>
                    <li>
                      <strong>2. Copy Credentials:</strong>
                      <br />Copy Endpoint URL and API Key from "Keys and Endpoint"
                    </li>
                    <li>
                      <strong>3. Update .env File:</strong>
                      <br />
                      <code className="bg-gray-100 px-2 py-1 rounded block mt-1">
                        AZURE_VOICE_LIVE_ENDPOINT=https://your-resource.cognitiveservices.azure.com/
                      </code>
                      <code className="bg-gray-100 px-2 py-1 rounded block mt-1">
                        AZURE_VOICE_LIVE_API_KEY=your-api-key-here
                      </code>
                    </li>
                    <li>
                      <strong>4. Restart Backend:</strong>
                      <br />Stop and restart the Python backend server
                    </li>
                  </ol>
                </div>

                <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded">
                  <p className="text-xs text-blue-800">
                    <strong>Note:</strong> Teacher Dashboard and other features are still accessible while voice features are being configured.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
